/**
 * Professional map-marker glyphs rendered from the app's Lucide icon set.
 * Each icon is rendered to an SVG string once per (icon, color, size) and
 * cached, so hundreds of markers reuse the same markup. Used by the 2D renderer
 * (SVG injected into the marker content) and the 3D renderer (an <img> glyph on
 * the affiliation-colored PinElement).
 */

import { createElement } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import type { LucideIcon } from 'lucide-react';
import { MARKER_ICON_BY_KEY } from './marker-icon-catalog';

/** Stroke weight for marker glyphs — slightly bolder than the UI default for legibility. */
const STROKE_WIDTH = 2.25;

/** The Lucide component for a layer manifest / location `icon` value, or undefined. */
export function lucideIconFor(icon: string | undefined): LucideIcon | undefined {
  return icon ? MARKER_ICON_BY_KEY.get(icon) : undefined;
}

/** Cache of rendered SVG strings keyed by `${icon}:${color}:${size}`. */
const svgCache = new Map<string, string>();

/** Render a Lucide icon to an SVG string (cached), or undefined for unknown keys. */
export function lucideSvgString(
  icon: string | undefined,
  color: string,
  size = 24,
): string | undefined {
  if (!icon) return undefined;
  const key = `${icon}:${color}:${size}`;
  const cached = svgCache.get(key);
  if (cached) return cached;

  const Icon = MARKER_ICON_BY_KEY.get(icon);
  if (!Icon) return undefined;

  const svg = renderToStaticMarkup(
    createElement(Icon, { size, color, strokeWidth: STROKE_WIDTH }),
  );
  svgCache.set(key, svg);
  return svg;
}

/** Render a Lucide icon as an <img> glyph for a 3D PinElement, or undefined. */
export function lucideImageElement(
  icon: string | undefined,
  color: string,
  size = 18,
): HTMLImageElement | undefined {
  const svg = lucideSvgString(icon, color, size);
  if (!svg) return undefined;

  const img = document.createElement('img');
  img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  img.width = size;
  img.height = size;
  img.style.pointerEvents = 'none';
  return img;
}
